import React from 'react';
import PropTypes from 'prop-types';
import { isEmpty, get, map } from 'lodash';
import Moment from 'moment';
import BookingsRequestMain from './bookings-request-main';
import { bookingRequestSubmitted, resetBookingStore, fetchBooking } from '../app-main/actions/booking';
import { resetProgress } from '../app-main/actions/flow';
import { resetInventoryStore } from '../app-main/actions/inventory';
import { getInitialScheduleVals, checkCCStateInterval } from './forms/bookingUtil';
import ProcessingPage from './ProcessingPage';
import publicConfig from '../../../public-config';
import SharedIntlProviderHOC from 'shared-components/hocs/sharedIntlProviderHOC';
import { checkCateringEligibility } from '../catering/catering-utils';

export class BookingsRequestFlow extends React.Component {
  constructor (props, context) {
    super(props, context);

    this.bookingRequestSent = this.bookingRequestSent.bind(this);
    this.redirectToInboxDetails = this.redirectToInboxDetails.bind(this);
    this.getCurrentSchedule = this.getCurrentSchedule.bind(this);
    this.ccProcessingCb = this.ccProcessingCb.bind(this);

    this.state = {
      isProcessingCC: false
    };
  }

  componentWillReceiveProps (nextProps, nextState) {
    // after submitting a booking request,
    // user only gets to `/bkc` page if booking is in "NO_AUTH" or "INQUIRED" state or "OFFER_CLOSED" (meaning offer is expired, declined or withdrawn)
    // else redirect to `inbox/:bookingID`
    const { booking, route, location } = nextProps;
    const { bookingRequestSubmitted } = booking;
    const bkg = booking.booking || {};
    const { obj_state, _id } = bkg;
    const bkgStatesThatNeedsToBeResubmitted = ['INQUIRED', 'NO_AUTH', 'OFFER_CLOSED', 'NO_CAPTURE'];
    const bookingHasBeenRequested = obj_state && (-1 === bkgStatesThatNeedsToBeResubmitted.indexOf(obj_state));
    const isOnBKCWithABookingID = ('bkc/:listingID(/:mobile)' === window.location.href) && get(location, 'query.request', {});
    if (bookingHasBeenRequested && isOnBKCWithABookingID && !bookingRequestSubmitted) {
      // !bookingRequestSubmitted means they came from /inbox, router is not used
      // this is to make sure we can show the booking request confirmation banner in concierge form
      this.context.router.push({ pathname: `/inbox/${_id}`});
    }
  }

  componentWillUnmount () {
    // reset values in store for booking and services to initialState
    // this is to make sure any code relying on store is not using stale data
    // NOTE: no longer resetting services. We need them to show banner in inbox
    // NOTE: no longer resetting booking in iosWebView since it causes errors
    const { dispatch, iosWebView } = this.props;
    if (!iosWebView) {
      dispatch(resetBookingStore());
    }
  }

  bookingRequestSent (booking, goToInbox) {
    const { dispatch, accessToken } = this.props;

    const bookingId = get(booking, '_id');
    dispatch(bookingRequestSubmitted(booking));

    this.setState({
      isProcessingCC: true,
      booking: booking,
      goToInbox: goToInbox
    });

    dispatch(resetProgress());
    dispatch(resetInventoryStore());
    checkCCStateInterval(bookingId, accessToken, this.ccProcessingCb);
  }

  redirectToInboxDetails () {
    const { params } = this.props;

    const bookingDetailsLink = `/inbox/${params.bookingID}`;
    /* update URL to direct them to the inbox */
    this.context.router.push(bookingDetailsLink);
  }

  getCurrentSchedule (schedule = []) {
    const currentSchedule = schedule.map((hash) => {
      let returnVal;
      if (!isEmpty(hash)) {
        const date = hash.start.date;
        const day = Moment(date.m + '-' + date.d + '-' + date.y, 'MM-DD-YYYY');
        if (day.isSameOrAfter(Moment().startOf('day'))) {
          returnVal = hash;
        }
      }
      return returnVal;
    });

    return currentSchedule;
  }

  ccProcessingCb (error) {
    const { booking, goToInbox } = this.state;
    const { dispatch, listing, accessToken } = this.props;
    const isInstantBooking = get(booking, 'is_instant_booking');
    const useType = get(listing, 'data.use_type');

    const bookingId = get(booking, '_id');
    const bookingState = get(booking, 'obj_state');

    dispatch(fetchBooking(bookingId, accessToken, resp => {
      if ('NO_AUTH' === resp.obj_state || error) {
        return this.context.router.push({ pathname: `/inbox/${bookingId}` });
      }
      const listingMetro = get(resp, 'listing.metro', '');
      const country = get(resp, 'listing.country').toLowerCase();
      const locale = SharedIntlProviderHOC.getLocale();

      const shouldDirectToCatering = checkCateringEligibility(
        listingMetro,
        booking?.number_of_guests || 0,
        booking?.start
      );

      if (isInstantBooking) {
        analyticsIntegrations.publicAttendee(
          'attendee details: view and share',
          {
            label: bookingId,
            origin: 'checkout'
          }
        );
      }

      let redirectUrl = '';
      if (shouldDirectToCatering) {
        redirectUrl = `/inbox/${bookingId}/catering`;
      } else if (isInstantBooking) {
        redirectUrl = `/invite/add/${bookingId}`;
      } else if (goToInbox) {
        redirectUrl =
          publicConfig.hideReferral ||'us' !== country || 'en-US' !== locale
            ? `/inbox/${bookingId}`
            : `/referrals/promotion/${bookingId}`;
      } else {
        redirectUrl = `/add-services/${bookingId}`;
      }

      window.location = redirectUrl;
    }));
  }

  render () {
    const { booking, services, route, previousLocation, iosWebView, params } = this.props;
    const listOfServices = services.services.data || [];
    const pathname = get(previousLocation, 'pathname', null);
    const prevLoc = pathname ? pathname.slice(1).split('/')[0] : null;
    const routePath = get(route, 'path', '');
    const bookingObj = get(booking, 'booking', {});

    let propsWithCallback;

    if (this.state.isProcessingCC) {
      return (
        <ProcessingPage />
      );
    }

    if ('bkc/:listingID(/:mobile)' === routePath) {
      const parsedSchedule = this.getCurrentSchedule(this.props.schedule);

      if ('inbox' === prevLoc) {
        return (
          !isEmpty(bookingObj) ?
            <BookingsRequestMain
              { ...this.props }
              prevLoc={prevLoc}
              submitCB={this.bookingRequestSent}
              parsedSchedule={parsedSchedule}
              getInitialScheduleVals={getInitialScheduleVals}
              initialScheduleValues={getInitialScheduleVals(parsedSchedule)}
            />
            :
            null
        );
      }

      return (
        <BookingsRequestMain
          { ...this.props }
          submitCB={this.bookingRequestSent}
          parsedSchedule={parsedSchedule}
          getInitialScheduleVals={getInitialScheduleVals}
          initialScheduleValues={getInitialScheduleVals(parsedSchedule)}
        />
      )
    } else if ('add-services/production-insurance/:bookingId' === routePath) {
      const bookingId = get(this.props, 'params.bookingId');
      window.location.href = `/inbox/${bookingId}`;
    } else if ('Invalid Permissions' === booking.error) {
      return (
        <div className="text-center" style={ { paddingTop: '22px' } }>
          <h3>There is nothing for you to do here.</h3>
          <a className="btn btn-danger" href="/inbox">Go to bookings</a>
        </div>
      );
    }

    return null;
  }
}

BookingsRequestFlow.propTypes = {
  booking: PropTypes.object.isRequired,
  route: PropTypes.object,
  previousLocation: PropTypes.object,
  params: PropTypes.object,
  services: PropTypes.object,
  dispatch: PropTypes.func,
  iosWebView: PropTypes.bool
};

BookingsRequestFlow.contextTypes = {
  router: PropTypes.object
};
